import { create } from 'zustand';
import { postClientRefresh } from './broadcast';
import { markTokenRotationOnNextCall } from '@/auth/auth-module';
import { getTracker } from '@/telemetry/tracker';

/**
 * Client-identity storage keys. Parity with the legacy Angular shell, which
 * keeps active/previous/temp client in localStorage only (client.service.ts,
 * base-route.guard.ts) regardless of the enableSessionStorage flag, so all
 * tabs, MFEs and the Angular shell resolve the same client.
 */
const LS_CURRENT = 'currentlySelectedClientId';
const LS_PREVIOUS = 'previouslySelectedClientId';
const LS_TEMP = 'tempClientId';

export interface ClientInfo {
  id: string;
  name: string;
  isFavorite: boolean;
}

export type ClientSwitchSource = 'user-switch' | 'url-change' | 'reset';

interface SetCurrentOptions {
  /** Origin of the switch — drives telemetry and broadcast payload. */
  source?: ClientSwitchSource;
  /** Skip cross-tab broadcast (used when applying a remote refresh). */
  silent?: boolean;
}

interface ClientState {
  current: ClientInfo | null;
  available: ClientInfo[];
  isSwitchPopupOpen: boolean;
  /**
   * The active client id known from the URL / localStorage BEFORE the client
   * list resolves `current`. Single source of truth for imperative,
   * non-reactive reads (SDK `clientModule`, `EY_RAPToR_getCurrentClientId`,
   * the iframe URL builder) so they never lag localStorage during a cold-start
   * deep link. Always mirrors `current.id` once `current` is set.
   */
  bootstrapClientId: string | null;
  /**
   * A direct/hard-URL navigation (address bar, external deep link, same-tab
   * paste) whose clientId differs from the persisted one, awaiting user
   * confirmation before the switch is finalised. Set by the route
   * `persistLegacyRouteClient`; consumed by `DirectClientChangeGuard`.
   */
  pendingDirectSwitch: { from: string; to: string } | null;
  setCurrent: (client: ClientInfo, options?: SetCurrentOptions) => void;
  setAvailable: (clients: ClientInfo[]) => void;
  /**
   * Seed/advance the bootstrap client id (and legacy storage) on the
   * cold-start / deep-link path, where the full `ClientInfo` is not available
   * yet so `setCurrent` cannot run. Keeps localStorage and the store in sync
   * from a single writer.
   */
  setBootstrapClientId: (clientId: string, previousId?: string | null) => void;
  resetToPrevious: () => void;
  setSwitchPopupOpen: (open: boolean) => void;
  setPendingDirectSwitch: (pending: { from: string; to: string } | null) => void;
  toggleFavorite: (clientId: string, isFavorite: boolean) => void;
}

/** Persist legacy client keys synchronously before any token call (D9). */
function persistClientIds(currentId: string, previousId: string | null): void {
  localStorage.setItem(LS_CURRENT, currentId);
  if (previousId) localStorage.setItem(LS_PREVIOUS, previousId);
  localStorage.removeItem(LS_TEMP);
}

/**
 * Seed the active client id from storage at store creation, so imperative
 * reads resolve a cold-start / deep-link client BEFORE the client list loads
 * and `current` is set (otherwise the SDK bridge and iframe URL builder would
 * see `null` while localStorage already holds the client — the cross-shell
 * desync closed by `bootstrapClientId`).
 */
function readInitialClientId(): string | null {
  try {
    return localStorage.getItem(LS_CURRENT);
  } catch {
    return null;
  }
}

/**
 * Holds the active client (multi-tenant context). Migrated from the Angular
 * `HeaderService`. On every commit:
 *  1. Write legacy storage keys synchronously (D9 — before any token call).
 *  2. Mark MSAL for forced refresh on the next acquireToken call.
 *  3. Post `clientRefresh_channel` so other tabs / old-shell pick up the switch.
 */
export const useClientStore = create<ClientState>((set, get) => ({
  current: null,
  available: [],
  isSwitchPopupOpen: false,
  bootstrapClientId: readInitialClientId(),
  pendingDirectSwitch: null,

  setCurrent: (client, options = {}) => {
    const prev = get().current;
    const previousId = prev?.id ?? null;
    if (prev?.id === client.id) {
      set({ current: client, bootstrapClientId: client.id });
      return;
    }

    persistClientIds(client.id, previousId);

    markTokenRotationOnNextCall();
    set({ current: client, bootstrapClientId: client.id });

    const source: ClientSwitchSource = options.source ?? 'user-switch';
    getTracker().event('client_switched', {
      clientId: client.id,
      previousClientId: previousId,
      source,
    });

    if (!options.silent) {
      postClientRefresh({ clientId: client.id, previousClientId: previousId, source });
    }
  },

  setAvailable: (clients) => set({ available: clients }),

  setBootstrapClientId: (clientId, previousId = null) => {
    try {
      localStorage.setItem(LS_CURRENT, clientId);
      if (previousId && previousId !== clientId) localStorage.setItem(LS_PREVIOUS, previousId);
    } catch {
      /* localStorage may be unavailable */
    }
    set({ bootstrapClientId: clientId });
  },

  resetToPrevious: () => {
    const prevId = localStorage.getItem(LS_PREVIOUS);
    if (!prevId) return;
    const prev = get().available.find((c) => c.id === prevId);
    if (prev) {
      // Local cancel of an in-flight switch — no broadcast (other tabs never
      // saw the new client).
      persistClientIds(prev.id, get().current?.id ?? null);
      set({ current: prev, bootstrapClientId: prev.id });
    }
  },

  setSwitchPopupOpen: (open) => set({ isSwitchPopupOpen: open }),

  setPendingDirectSwitch: (pending) => set({ pendingDirectSwitch: pending }),

  toggleFavorite: (clientId, isFavorite) =>
    set((state) => ({
      available: state.available.map((c) => (c.id === clientId ? { ...c, isFavorite } : c)),
    })),
}));

/** Storage keys re-exported so the SDK shim (M2.9) can mirror legacy reads. */
export const CLIENT_STORAGE_KEYS = {
  localCurrent: LS_CURRENT,
  localPrevious: LS_PREVIOUS,
  localTemp: LS_TEMP,
} as const;

/**
 * Effective active client id for imperative / cross-shell reads: the resolved
 * `current` client, else the bootstrap id seeded from the URL / localStorage
 * before the client list loads. Reactive React components should keep reading
 * `current` directly (they re-render once it resolves); this helper is for
 * non-reactive callers (SDK bridge, `EY_RAPToR_getCurrentClientId`, the iframe
 * URL builder) that would otherwise read `null` during a cold-start deep link.
 */
export function getActiveClientId(): string | null {
  const s = useClientStore.getState();
  return s.current?.id ?? s.bootstrapClientId ?? null;
}
